import apiV2Service from '../api/api-v2.service';
import {LocationSet, LocationSetSignal, RemoteLocationSet} from './locationSets.types';
import {toClientLocationSet} from './locationSets.dataConverter';
import {handleApiError} from '../errorHandling/handleApiError';
import {FeedItem} from '../feedItems/feedItems.types';
import _map from 'lodash/map';
import {toStoreFeedItem} from '../feedItems/feedItems.dataConverters';
import {ResponseMetaData} from '../api/apiTypes';
import waitForJob from '../jobs/utils/waitForJob';

const basePath = `LocationSets`;

function getLocationSets(locationFeedId: number) {
  return apiV2Service
    .executeGetRequest<RemoteLocationSet[]>(`${basePath}/${locationFeedId}/GetLocationSets`)
    .then(({data: locationSets}) => locationSets.map(toClientLocationSet))
    .catch(handleApiError);
}

function createLocationSet(locationSet: LocationSet) {
  return apiV2Service
    .executePutRequest<{
      locationSetId: number;
      updateItemsJobId: number;
    }>(`${basePath}/CreateLocationSet`, {}, {...locationSet})
    .then(({data: {updateItemsJobId}}) =>
      waitForJob({
        jobId: updateItemsJobId,
        jobName: `Create location set`,
      }),
    )
    .catch(handleApiError);
}

function updateLocationSet(locationSet: LocationSet) {
  return apiV2Service
    .executePutRequest<number>(`${basePath}/UpdateLocationSet`, {}, {...locationSet})
    .then(({data: jobId}) =>
      waitForJob({jobId, jobName: `Update location set LS${locationSet.id}`}),
    )
    .catch(handleApiError);
}

function deleteLocationSets(locationFeedId: number, locationSetIds: number[]) {
  return apiV2Service.executeDeleteRequest<number>(
    `${basePath}/${locationFeedId}/DeleteLocationSets`,
    {
      locationSetIds,
    },
  );
}

function fetchSignals(locationFeedId: number) {
  return apiV2Service
    .executeGetRequest<LocationSetSignal[]>(`${basePath}/${locationFeedId}/signals`)
    .then((res) => res.data);
}

function getOptionalValues(locationFeedId: number, signalKey: string, searchPattern: string) {
  return apiV2Service
    .executeGetRequest<string[]>(`${basePath}/${locationFeedId}/suggestions`, {
      signalKey,
      searchPattern,
    })
    .then((res) => res.data);
}

function removeNonExistingLocations(locationSetId: number) {
  return apiV2Service
    .executePatchRequest<number>(
      `${basePath}/RemoveNonExistingLocations/${locationSetId}`,
      {},
      {locationSetId},
    )
    .then(({data: jobId}) =>
      waitForJob({
        jobId,
        jobName: `Remove non existing items from location set LS${locationSetId}`,
      }),
    )
    .catch(handleApiError);
}

async function getLocationSetItems(feedId: number, locationSetId: number) {
  const {data} = await apiV2Service.executeGetRequest<{
    items: FeedItem[];
    metadata: ResponseMetaData;
  }>(`${basePath}/${feedId}/GetLocationSetItems/${locationSetId}`);

  return {
    feedItems: _map(data.items, toStoreFeedItem),
    meta: data.metadata,
  };
}

async function searchLocationSets(prompt: string) {
  const {data} = await apiV2Service.executeGetRequest<{
    locationSets: Array<{id: number; feedId: number; name: string}>;
    filtersExplanation: string;
  }>(`${basePath}/SearchLocationSets`, {prompt});

  return data;
}

const locationSetsApi = {
  getLocationSets,
  createLocationSet,
  updateLocationSet,
  deleteLocationSets,
  fetchSignals,
  getOptionalValues,
  removeNonExistingLocations,
  getLocationSetItems,
  searchLocationSets,
};

export default locationSetsApi;
